// src/modules/moduleDashboards/pages/ModuleDashboardPage.jsx
// The application's only dashboard. It renders the layout an admin BUILT for the
// viewer's role in the Dashboard Builder — dashboards are 1:1 with role permission
// sets, and the server resolves which one this user gets (plus their max data
// scope) before returning data. The legacy hardcoded /dashboard has been retired;
// this page carries its behaviour forward:
//
//   • refreshProfile() on mount   — permissions/dashboard binding may have changed
//   • loadDashboardData()         — single fetch used by filters, refresh and sockets
//   • isAdmin / isPrivileged      — branch-scoped viewers are locked to their branch
//   • notificationStream          — auto-refreshes when an incoming event
//                                   changes the numbers on screen
//                                   when an approval or billing event lands
//
// A role with no published layout gets an empty state (admins get a shortcut into
// the builder) — there is no hardcoded fallback dashboard any more.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Alert, message, Card, Empty, Button, Space, Typography, DatePicker } from 'antd';
import { AppstoreOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import ContextSelector from '../components/ContextSelector';
import BuiltDashboard from '../components/BuiltDashboard';
import { fetchRegions, fetchDashboardBranches, renderBuiltDashboard } from '../api';
import notificationStream from '../../notifications/stream';

const { Text, Title } = Typography;

// Socket message types that are control signals, not user-facing notifications.

export default function ModuleDashboardPage() {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const maxScope = user?.dashboard?.maxScope || (isAdmin ? 'GLOBAL' : 'BRANCH');
  // Privileged = admin OR allowed to reach beyond their own branch.
  const isPrivileged = isAdmin || (maxScope && maxScope !== 'BRANCH');

  const [scope, setScope] = useState('BRANCH');
  const [branch, setBranch] = useState('all');
  const [region, setRegion] = useState(null);
  const [dateRange, setDateRange] = useState({});

  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);

  const [built, setBuilt] = useState(null);   // { widgets, name, label } once a layout renders
  const [noLayout, setNoLayout] = useState(null); // { dashboardLabel, reason } when nothing is built
  const [appliedLabel, setAppliedLabel] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  // Re-fetch effective permissions on mount so navigating back here always
  // reflects the latest access — including a dashboard an admin just rebuilt.
  useEffect(() => {
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selector options only matter for privileged users.
  useEffect(() => {
    if (!isPrivileged) return;
    fetchDashboardBranches().then(setBranches).catch(() => {});
    fetchRegions().then(setRegions).catch(() => {});
  }, [isPrivileged]);

  // Branch-scoped viewers are pinned to their own branch — the server enforces
  // this too, but keep the request honest so the UI never implies otherwise.
  useEffect(() => {
    if (!isPrivileged) {
      setScope('BRANCH');
      setBranch('all');
    }
  }, [isPrivileged]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const rendered = await renderBuiltDashboard({
        dateRange,
        branch: isPrivileged ? branch : undefined,
      });

      if (rendered?.hasLayout) {
        setBuilt({
          widgets: rendered.widgets || [],
          name: rendered.name,
          label: rendered.dashboardLabel,
        });
        setNoLayout(null);
        setAppliedLabel(isPrivileged ? null : 'My Branch');
      } else {
        setBuilt(null);
        setNoLayout({
          dashboardLabel: rendered?.dashboardLabel || null,
          reason: rendered?.reason || 'no-layout',
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load dashboard');
      messageApi.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, branch, isPrivileged, messageApi]);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  // Refresh the dashboard when an incoming notification changes the numbers on
  // screen. It no longer toasts: NotificationProvider owns the toast for every
  // screen, and doing it here as well produced two popups for one event.
  useEffect(() => {
    const unsub = notificationStream.subscribe(({ event, payload }) => {
      try {
        if (event !== 'notification.created') return;
        const type = payload?.type || '';
        if (type.startsWith('approval') || payload?.metadata?.module === 'billing') {
          loadDashboardData();
        }
      } catch (e) {
        console.warn('[Dashboard] Error handling a live notification', e);
      }
    });
    return () => { try { unsub && unsub(); } catch (e) { /* already gone */ } };
  }, [loadDashboardData]);

  const rangeValue = useMemo(() => ([
    dateRange.startDate ? dayjs(dateRange.startDate) : null,
    dateRange.endDate ? dayjs(dateRange.endDate) : null,
  ]), [dateRange]);

  const handleDateChange = (_, strs) => {
    setDateRange(strs?.[0] && strs?.[1] ? { startDate: strs[0], endDate: strs[1] } : {});
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent', padding: '8px 4px' }}>
      {contextHolder}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap align="center">
          <Text strong><CalendarOutlined /> Period:</Text>
          <DatePicker.RangePicker
            value={rangeValue}
            onChange={handleDateChange}
            allowClear
            style={{ minWidth: 260 }}
          />
          {!dateRange.startDate && <Text type="secondary" style={{ fontSize: 12 }}>Each widget uses its own configured window</Text>}
        </Space>
      </Card>

      <ContextSelector
        maxScope={maxScope}
        branchLocked={!isPrivileged}
        scope={scope}
        onScopeChange={setScope}
        branch={branch}
        onBranchChange={setBranch}
        branches={branches}
        region={region}
        onRegionChange={setRegion}
        regions={regions}
        appliedLabel={appliedLabel}
        loading={loading}
        onRefresh={loadDashboardData}
      />

      {error && (
        <Alert type="error" showIcon closable style={{ marginBottom: 16 }}
          message="Error loading dashboard" description={error} onClose={() => setError(null)} />
      )}

      {noLayout && !loading ? (
        <Card style={{ borderRadius: 12, marginTop: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={(
              <Space direction="vertical" size={4}>
                <Title level={5} style={{ margin: 0 }}>No dashboard configured yet</Title>
                <Text type="secondary">
                  {noLayout.reason === 'no-dashboard-bound'
                    ? 'Your role has no dashboard assigned. Ask an administrator to build one in the Dashboard Builder.'
                    : `No widgets have been published for ${noLayout.dashboardLabel ? `the “${noLayout.dashboardLabel}” role` : 'your role'} yet.`}
                </Text>
              </Space>
            )}
          >
            {isAdmin && (
              <Button type="primary" icon={<AppstoreOutlined />}
                onClick={() => navigate('/module-dashboards/builder')}>
                Open Dashboard Builder
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <BuiltDashboard widgets={built?.widgets || []} loading={loading} onRefresh={loadDashboardData} />
      )}
    </Layout>
  );
}
